import { getPreference } from '@fs/zion-preferences'
import { i18n } from '@fs/zion-locale'

const utils = {
  MAX_CHARS_TITLE: 20,
  MAX_CHARS_NOTES: 3000,

  // Preference keys
  PREFERENCE_KEYS: {
    changes: 'contributions.group',
    direction: 'following.direction',
    group: 'following.group',
    newToFollowing: 'following.newToFollowing',
    myChanges: 'following.my_changes',
    privateDirection: 'private.direction',
    privateSort: 'private.sort',
    sort: 'following.sort',
    'following.alert': 'following.alert-banner.dismissed-labels',
    'contributions.alert': 'contributions.alert-banner.dismissed',
  },

  // Gender Constants
  GENDER_MALE: 'MALE',
  GENDER_FEMALE: 'FEMALE',
  GENDER_UNKOWN: 'UNKNOWN',

  // Arbitrary date year beyond now for sorting people without a birthdate.
  NO_BIRTH_DATE: 3000,

  // Page Tab Constants
  STATS: 0,
  MY_CHANGES: 1,
  PRIVATE_PERSONS: 2,

  // ChoiceChip View By Constants
  PERSON: 'PERSON',
  CHRONOLOGICAL: 'CHRONOLOGICAL',

  // ChoiceChip Sort Constants
  SORT_DATE_CHANGED: 'dateChanged',
  SORT_NAME: 'fullName',
  SORT_FIRST_NAME: 'givenName',
  SORT_LAST_NAME: 'surname',
  SORT_ID_NUMBER: 'personId',
  SORT_DATE_OF_BIRTH: 'birth',
  SORT_DATE_OF_DEATH: 'death',
  SORT_SEX: 'gender',
  SORT_DATE: 'changeDate',
  SORT_ASCENDING: 'asc',
  SORT_DESCENDING: 'desc',

  // ChoiceChip Filter Constants
  FILTER_ALL: null,
  FILTER_ACTIVELY_CHANGING: 'ACTIVELY_CHANGING',
  FILTER_COMPLETED: 'COMPLETED',
  FILTER_END_OF_LINE: 'END_OF_LINE',
  FILTER_INTERESTED_IN: 'INTERESTED_IN',
  FILTER_RESEARCHING: 'RESEARCHING',
  FILTER_STUCK: 'STUCK',
  FILTER_TEMPLE_YEAR_RULE: 'TEMPLE_110_YEAR',
  FILTER_TEMPLE_RESERVATION: 'TEMPLE_RESERVATION',
  FILTER_CUSTOM: 'CUSTOM',

  // Label Constants
  LABEL_SHOW_ALL: 'all',
  LABEL_SHOW_FILTERABLE: 'filterable',

  // ChoiceChip Show My Changes
  SHOW_MY_CHANGES: 'SHOW_MY_CHANGES',
  HIDE_MY_CHANGES: 'HIDE_MY_CHANGES',

  // Chart Type Constants
  CHART_ALL: 'ALL',
  CHART_SOURCES: 'SOURCES',
  CHART_MEMORIES: 'MEMORIES',
  CHART_PERSONS: 'PERSONS',

  supportedContributionTypes: {
    COPY_PERSON: true,
    CREATE_PERSON: true,
    CREATE_COUPLE_RELATIONSHIP: true,
    CREATE_PARENT_CHILD_RELATIONSHIP: true,
    CREATE_ASSOCIATION_RELATIONSHIP: true,

    TOMBSTONE_PERSON: true,
    TOMBSTONE_COUPLE_RELATIONSHIP: true,
    TOMBSTONE_PARENT_CHILD_RELATIONSHIP: true,
    TOMBSTONE_ASSOCIATION_RELATIONSHIP: true,

    RESTORE_PERSON: true,
    MAKE_LIVING: true,

    ADD_DISMISSED_SUGGESTION: true,
    ADD_DISMISSED_COUPLE_SUGGESTION: true,
    ADD_DISMISSED_PARENT_CHILD_SUGGESTION: true,
    ADD_DISMISSED_ASSOCIATION_SUGGESTION: true,
    DELETE_DISMISSED_SUGGESTION: true,
    DELETE_DISMISSED_COUPLE_SUGGESTION: true,
    DELETE_DISMISSED_PARENT_CHILD_SUGGESTION: true,
    DELETE_DISMISSED_ASSOCIATION_SUGGESTION: true,

    MERGE_PERSON: true,
    UNMERGE_PERSON: true,

    RESTORE_COUPLE_RELATIONSHIP: true,
    RESTORE_PARENT_CHILD_RELATIONSHIP: true,
    RESTORE_ASSOCIATION_RELATIONSHIP: true,

    // COUPLE_PERSON_ADDED: true,
    // PARENT_CHILD_PERSON_ADDED: true,
    // ASSOCIATION_PERSON_ADDED: true,

    REPLACE_COUPLE_PERSON_ID: true,
    REPLACE_PARENT_CHILD_PERSON_ID: true,
    REPLACE_ASSOCIATION_PERSON_ID: true,
    SWAP_SPOUSE_IDS: true,
    SWAP_PARENT_IDS: true,
    SWAP_PERSON_IDS: true,

    ADD_CONCLUSION: true,
    ADD_COUPLE_CONCLUSION: true,
    ADD_PARENT_CHILD_CONCLUSION: true,
    ADD_ASSOCIATION_CONCLUSION: true,
    UPDATE_CONCLUSION: true,
    UPDATE_COUPLE_CONCLUSION: true,
    UPDATE_PARENT_CHILD_CONCLUSION: true,
    UPDATE_ASSOCIATION_CONCLUSION: true,
    DELETE_CONCLUSION: true,
    DELETE_COUPLE_CONCLUSION: true,
    DELETE_PARENT_CHILD_CONCLUSION: true,
    DELETE_ASSOCIATION_CONCLUSION: true,

    ADD_ENTITY_REF: true,
    ADD_COUPLE_ENTITY_REF: true,
    ADD_PARENT_CHILD_ENTITY_REF: true,
    ADD_ASSOCIATION_ENTITY_REF: true,
    UPDATE_ENTITY_REF: true,
    UPDATE_COUPLE_ENTITY_REF: true,
    UPDATE_PARENT_CHILD_ENTITY_REF: true,
    UPDATE_ASSOCIATION_ENTITY_REF: true,
    DELETE_ENTITY_REF: true,
    DELETE_COUPLE_ENTITY_REF: true,
    DELETE_PARENT_CHILD_ENTITY_REF: true,
    DELETE_ASSOCIATION_ENTITY_REF: true,

    ADD_NOTE: true,
    ADD_COUPLE_NOTE: true,
    ADD_PARENT_CHILD_NOTE: true,
    ADD_ASSOCIATION_NOTE: true,
    UPDATE_NOTE: true,
    UPDATE_COUPLE_NOTE: true,
    UPDATE_PARENT_CHILD_NOTE: true,
    UPDATE_ASSOCIATION_NOTE: true,
    DELETE_NOTE: true,
    DELETE_COUPLE_NOTE: true,
    DELETE_PARENT_CHILD_NOTE: true,
    DELETE_ASSOCIATION_NOTE: true,

    // AFFECTED_BY_MERGE_PERSON: true,
    // AFFECTED_BY_UNMERGE_PERSON: true,
    // AFFECTED_BY_TOMBSTONE_PERSON: true,
    // AFFECTED_BY_RESTORE_PERSON: true,

    UNKNOWN: true,
  },

  localizeDate: (time) => {
    const timeStamp = new Date(time)?.getTime()
    // make sure we have a timeStamp
    if (!timeStamp) return time
    const dateConfig = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }
    return new Date(parseInt(timeStamp, 10)).toLocaleDateString(i18n.language, dateConfig)
  },

  getChangePersonId: (change) => change.personId ?? change.childId ?? change.spouse1Id ?? change.spouse2Id ?? null,

  getTranslatedUrl: (isFollowingPage) => {
    let url
    if (isFollowingPage) {
      switch (i18n.language) {
        case 'de':
          url = 'https://www.familysearch.org/blog/de/add-labels-following-family-tree/'
          break
        case 'es':
          url = 'https://www.familysearch.org/blog/es/agregar-etiquetas-seguimiento-arbol-familiar/'
          break
        case 'fr':
          url = 'https://www.familysearch.org/blog/fr/ajouter-des-etiquettes-aux-fiches-suivies-dans-larbre-familial/'
          break
        case 'it':
          url = 'https://www.familysearch.org/blog/it/aggiungi-etichette-following-albero-familiare/'
          break
        case 'ja':
          url = 'https://www.familysearch.org/blog/jp/add-labels-following-family-tree/'
          break
        case 'ko':
          url = 'https://www.familysearch.org/blog/ko/add-labels-following-family-tree/'
          break
        case 'pt':
          url = 'https://www.familysearch.org/blog/pt/acrescentar-descricao-seguindo-arvore-familiar/'
          break
        case 'ru':
          url = 'https://www.familysearch.org/blog/ru/add-labels-following-family-tree/'
          break
        case 'zh':
          url = 'https://www.familysearch.org/blog/zh/add-labels-following-family-tree/'
          break
        default:
          // Default to English
          url = 'https://www.familysearch.org/blog/en/familysearch-updates-2020/#add-labels'
      }
    } else {
      switch (i18n.language) {
        case 'de':
          url = 'https://www.familysearch.org/blog/de/meine-einreichungen/'
          break
        case 'es':
          url = 'https://www.familysearch.org/blog/es/mis-aportaciones/'
          break
        case 'fr':
          url = 'https://www.familysearch.org/blog/fr/mes-contributions/'
          break
        case 'it':
          url = 'https://www.familysearch.org/blog/it/i-miei-contributi/'
          break
        case 'ja':
          url = 'https://www.familysearch.org/blog/jp/my-contributions/'
          break
        case 'ko':
          url = 'https://www.familysearch.org/blog/ko/my-contributions/'
          break
        case 'pt':
          url = 'https://www.familysearch.org/blog/pt/minhas-contribuicoes/'
          break
        case 'ru':
          url = 'https://www.familysearch.org/blog/ru/my-contributions/'
          break
        case 'zh':
          url = 'https://www.familysearch.org/blog/zh/my-contributions/'
          break
        default:
          // Default to English
          url = 'https://www.familysearch.org/blog/en/familysearch-updates/#contributions-desktop'
      }
    }
    return url
  },

  loadNextPage: ({ endpointCall, nextPageToken, setData, setIsFetching }) => {
    // Handling InfiniteScroll issue: tries to load more after screen resize. Jump out if nextPageToken is null.
    if (!nextPageToken) return

    // Reload page if user is not logged in (stale session)
    setIsFetching(true)
    endpointCall
      .then(setData)
      .catch(console.error)
      .finally(() => setIsFetching(false))
  },
}

getPreference('tree.showNamesInRomanScript')
  .then((pref) => {
    utils.showNamesInRomanScript = pref
  })
  .catch(() => {
    utils.showNamesInRomanScript = false
  })

export default utils
